import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Shield,
    CheckCircle,
    XCircle,
    User,
    FileText,
    Image as ImageIcon,
    Calendar,
    AlertCircle,
    Clock,
    X
} from 'lucide-react';
import { useGetMarketplaceKycRequestsQuery, useUpdateMarketplaceKycStatusMutation } from '../../store/slices/apiSlice';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../config/api';

const MarketplaceKYCDetails: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const kycRequestId = id ? parseInt(id) : null;
    const isValidId = kycRequestId && !isNaN(kycRequestId) && kycRequestId > 0;

    const { data: requestsData, isLoading, error, refetch } = useGetMarketplaceKycRequestsQuery({
        page: 1,
        limit: 1000
    });

    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [adminComments, setAdminComments] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
    const [selectedImageTitle, setSelectedImageTitle] = useState<string>('');
    const [updateKycStatus, { isLoading: isUpdating }] = useUpdateMarketplaceKycStatusMutation();

    const requests = requestsData?.data?.requests || [];
    const request = requests.find(r => r.kyc_request_id === kycRequestId);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return <Badge variant="warning" className="flex items-center gap-1"><Clock className="w-3 h-3" />Pending</Badge>;
            case 'approved':
                return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Approved</Badge>;
            case 'rejected':
                return <Badge variant="danger" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Rejected</Badge>;
            default:
                return <Badge variant="default">{status}</Badge>;
        }
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    const getImageUrl = (url: string | null | undefined) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        const cleanUrl = url.startsWith('/') ? url : `/${url}`;
        return `${API_BASE_URL}${cleanUrl}`;
    };

    const handleApprove = () => {
        setAdminComments('');
        setShowApproveModal(true);
    };

    const handleReject = () => {
        setAdminComments('');
        setShowRejectModal(true);
    };

    const handleSaveApproval = async () => {
        if (!request) return;

        try {
            await updateKycStatus({
                kyc_request_id: request.kyc_request_id,
                status: 'approved',
                admin_comments: adminComments.trim() || undefined
            }).unwrap();
            toast.success('KYC request approved successfully');
            setShowApproveModal(false);
            setAdminComments('');
            refetch();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to approve KYC request');
        }
    };

    const handleSaveRejection = async () => {
        if (!request) return;

        if (!adminComments.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }

        try {
            await updateKycStatus({
                kyc_request_id: request.kyc_request_id,
                status: 'rejected',
                admin_comments: adminComments.trim()
            }).unwrap();
            toast.success('KYC request rejected');
            setShowRejectModal(false);
            setAdminComments('');
            refetch();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to reject KYC request');
        }
    };

    const handleViewImage = (imageUrl: string, title: string) => {
        setSelectedImageUrl(imageUrl);
        setSelectedImageTitle(title);
        setShowImageModal(true);
    };

    if (!isValidId) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Invalid KYC request ID</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error || !request) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Error loading KYC request. Please try again.</p>
                    <button
                        onClick={() => navigate('/admin/marketplace-kyc-requests')}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/marketplace-kyc-requests')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">KYC Request Details</h1>
                                <p className="text-gray-600">Request ID: #{request.kyc_request_id}</p>
                            </div>
                        </div>
                        {request.status === 'pending' && (
                            <div className="flex gap-3">
                                <Button
                                    onClick={handleApprove}
                                    variant="primary"
                                >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Approve
                                </Button>
                                <Button
                                    onClick={handleReject}
                                    variant="danger"
                                >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Reject
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* User Information */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <User className="w-5 h-5 mr-2" />
                                User Information
                            </h2>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Name</label>
                                <p className="mt-1 text-sm text-gray-900">{request.user?.name || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Email</label>
                                <p className="mt-1 text-sm text-gray-900">{request.user?.email || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">KYC Status</label>
                                <div className="mt-1">
                                    <Badge variant="default" className="capitalize">{request.user?.marketplace_kyc_status || 'N/A'}</Badge>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Request Information */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Shield className="w-5 h-5 mr-2" />
                                Request Information
                            </h2>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Status</label>
                                <div className="mt-1">{getStatusBadge(request.status)}</div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Document Type</label>
                                <p className="mt-1 text-sm text-gray-900 capitalize">{request.document_type || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Created At</label>
                                <div className="mt-1 flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <p className="text-sm text-gray-900">{formatDate(request.created_at)}</p>
                                </div>
                            </div>
                            {request.reviewed_at && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Reviewed At</label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{formatDate(request.reviewed_at)}</p>
                                    </div>
                                </div>
                            )}
                            {request.reviewed_by && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Reviewed By</label>
                                    <p className="mt-1 text-sm text-gray-900">{request.reviewed_by}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Documents */}
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-gray-900 flex items-center">
                                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                                KYC Verification Documents
                            </h2>
                            <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100">
                                {request.document_type || 'ID Card'}
                            </Badge>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                {[
                                    { key: 'document_front', label: 'Document Front', url: request.document_front },
                                    { key: 'document_back', label: 'Document Back', url: request.document_back },
                                    { key: 'address_proof', label: 'Address Proof', url: request.address_proof },
                                    { key: 'selfie_image', label: 'Selfie Image', url: request.selfie_image },
                                ].map((doc) => {
                                    if (!doc.url) return null;
                                    const fullUrl = getImageUrl(doc.url);
                                    
                                    return (
                                        <div key={doc.key} className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <label className="text-sm font-bold text-gray-800">{doc.label}</label>
                                                <button 
                                                    onClick={() => window.open(fullUrl, '_blank')}
                                                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 font-medium"
                                                >
                                                    <Download className="w-3 h-3" />
                                                    Full View
                                                </button>
                                            </div>
                                            <div 
                                                className="group relative cursor-pointer rounded-xl overflow-hidden border-2 border-gray-100 hover:border-blue-500 hover:shadow-xl transition-all duration-300 bg-gray-50 aspect-[4/3] flex items-center justify-center"
                                                onClick={() => handleViewImage(fullUrl, doc.label)}
                                            >
                                                <img
                                                    src={fullUrl}
                                                    alt={doc.label}
                                                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
                                                    }}
                                                />
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                                                    <div className="bg-white/90 p-3 rounded-full opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 shadow-xl">
                                                        <ImageIcon className="w-6 h-6 text-blue-600" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            
                            {(!request.document_front && !request.document_back && !request.address_proof && !request.selfie_image) && (
                                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                    <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                                    <p className="text-gray-500 font-medium">No documents have been uploaded for this request.</p>
                                </div>
                            )}
                            
                            <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-center text-xs text-gray-400 gap-4">
                                <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Secure Viewer</span>
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Click to expand</span>
                            </div>
                        </div>
                    </div>

                    {/* Admin Comments */}
                    {request.admin_comments && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <FileText className="w-5 h-5 mr-2" />
                                    Admin Comments
                                </h2>
                            </div>
                            <div className="p-6">
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{request.admin_comments}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Approve Modal */}
            <Modal
                isOpen={showApproveModal}
                onClose={() => {
                    setShowApproveModal(false);
                    setAdminComments('');
                }}
                title="Approve KYC Request"
            >
                <div className="p-6 space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <p className="text-sm text-green-800">
                                Approving KYC for {request.user?.name || 'user'}. This will update their KYC status to approved.
                            </p>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Admin Comments (Optional)</label>
                        <textarea
                            value={adminComments}
                            onChange={(e) => setAdminComments(e.target.value)}
                            rows={4}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter any comments..."
                        />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            onClick={() => {
                                setShowApproveModal(false);
                                setAdminComments('');
                            }}
                            variant="outline"
                            disabled={isUpdating}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveApproval}
                            variant="primary"
                            disabled={isUpdating}
                            isLoading={isUpdating}
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Reject Modal */}
            <Modal
                isOpen={showRejectModal}
                onClose={() => {
                    setShowRejectModal(false);
                    setAdminComments('');
                }}
                title="Reject KYC Request"
            >
                <div className="p-6 space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-600" />
                            <p className="text-sm text-red-800">
                                Rejecting KYC for {request.user?.name || 'user'}. Please provide a reason for rejection.
                            </p>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason *</label>
                        <textarea
                            value={adminComments}
                            onChange={(e) => setAdminComments(e.target.value)}
                            rows={4}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter reason for rejection..."
                            required
                        />
                        <p className="mt-1 text-xs text-gray-500">This reason will be sent to the user</p>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            onClick={() => {
                                setShowRejectModal(false);
                                setAdminComments('');
                            }}
                            variant="outline"
                            disabled={isUpdating}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveRejection}
                            variant="danger"
                            disabled={isUpdating || !adminComments.trim()}
                            isLoading={isUpdating}
                        >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Image Modal */}
            {showImageModal && selectedImageUrl && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4">
                    <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
                        {/* Close Button */}
                        <button
                            onClick={() => {
                                setShowImageModal(false);
                                setSelectedImageUrl('');
                                setSelectedImageTitle('');
                            }}
                            className="absolute top-4 right-4 z-10 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-all"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        {/* Image */}
                        <div className="relative max-w-full max-h-full flex items-center justify-center">
                            <img
                                src={selectedImageUrl}
                                alt={selectedImageTitle}
                                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MarketplaceKYCDetails;

